// External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

// Local libs
import CreditUsageTrackerModel from './creditUsageTracker.model';
import Service from '../common/service';

/**
 * Service for managing Credits Usage Tracker data.
 *
 * Provides functionality to fetch and manage credit usage tracker details.
 *
 * @module CreditsUsageTrackerService
 */
@Injectable({
  providedIn: 'root'
})
export class CreditsUsageTrackerService extends Service<CreditUsageTrackerModel> {

  /** Store ongoing HTTP request subscriptions */
  public ongoingRequests: Subscription[] = [];

  constructor(httpClient: HttpClient, router: Router) {
    super(httpClient, '/tradingcommunity/creditsusagetracker/');
  }

  /**
   * Retrieve usage tracker details for a subscriber with offset-based pagination
   * @param subscriberId The ID of the subscriber
   * @param page The record offset (default 0)
   * @param pageSize The number of records to return (default 20)
   * @returns Promise<Array<CreditUsageTrackerModel>>
   */
  public getSubscriberUsageTrackerById(subscriberId: string, page: number = 0, pageSize: number = 20): Promise<Array<CreditUsageTrackerModel>> {
    return new Promise((resolve, reject) => {
      const request = this.httpClient
        .get<Array<CreditUsageTrackerModel>>(
          `${this.baseUrl}${this.serviceUrl}?creditsManagerID=${subscriberId}&order=new&page=${page}&pageSize=${pageSize}`,
          this.httpOptions
        )
        .subscribe({
          next: (response) => {
            this.removeOngoingRequest(request);
            resolve(response);
          },
          error: (error: HttpErrorResponse) => {
            this.removeOngoingRequest(request);
            reject(error);
          }
        });

      this.ongoingRequests.push(request);
    });
  }

  /**
   * Retrieve usage tracker details for a BluEye subscriber with offset-based pagination
   * @param subscriberId The ID of the subscriber
   * @param page The record offset (default 0)
   * @param pageSize The number of records to return (default 20)
   * @returns Promise<Array<CreditUsageTrackerModel>>
   */
  public getBluEyeSubscriberUsageTrackerById(subscriberId: string, page: number = 0, pageSize: number = 20): Promise<Array<CreditUsageTrackerModel>> {
    return new Promise((resolve, reject) => {
      const request = this.httpClient
        .get<Array<CreditUsageTrackerModel>>(
          `${this.baseUrl}${this.serviceUrl}?creditsManagerID=${subscriberId}&order=new&page=${page}&pageSize=${pageSize}&conceal=bluEyeTrack.imagesDetails.imageHeatMap`,
          this.httpOptions
        )
        .subscribe({
          next: (response) => {
            this.removeOngoingRequest(request);
            resolve(response);
          },
          error: (error: HttpErrorResponse) => {
            this.removeOngoingRequest(request);
            reject(error);
          }
        });

      this.ongoingRequests.push(request);
    });
  }

  /**
   * Remove a completed or errored subscription from ongoing requests
   */
  private removeOngoingRequest(request: Subscription): void {
    const index = this.ongoingRequests.indexOf(request);
    if (index > -1) {
      this.ongoingRequests.splice(index, 1);
    }
  }

  /**
   * Cancel all ongoing HTTP requests to prevent memory leaks
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach(req => req?.unsubscribe?.());
    this.ongoingRequests = [];
  }
}